import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { FirstPage } from '@mui/icons-material';
import { IconButton, Tab, Tabs, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { format } from 'date-fns';
import {
  EstateWorkItem,
  EstateWorkItem_TaskQueueDataGridPageFilter,
  EstateWorkItemStatus,
  PortalPermissions,
  TaskQueueDataGridColumns,
  useAuth_ImpersonateMutation,
  useEstateWorkItem_GetAllEstateWorkItemsForTaskQueueDataGridLazyQuery,
  usePortalMembership_GetPortalMembershipsByPortalIdLazyQuery,
} from 'queries';

import { ServerEnabledDataGrid } from 'common/server-enabled-data-grid';
import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';
import { EstateWorkItemStatusLabel, EstateWorkItemSubStatusLabel } from 'utils/estate';
import { GridStateKey } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';

import AdminPointPerson from './admin-point-person';
import EstateWorkItemActionNeededCell from './estate-work-item-action-needed-cell';
import EstateWorkItemDrawer from './estate-work-item-drawer';
import EstateWorkItemTaskCell from './estate-work-item-task-cell';
import EstateWorkItemTaskStatusCell from './estate-work-item-task-status-cell';
import EstateWorkItemTaskSubStatusCell from './estate-work-item-task-sub-status-cell';
import EstateWorkItemEstateCell from './estate-work-items-estate-cell';
import TaskListCustomToolbar from './task-list-custom-toolbar';

const useStyles = makeStyles(() => ({
  root: {
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '&.MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainer': {
      paddingRight: 0,
      paddingLeft: 0,
    },
    width: '100%',
  },
  firstPageIcon: {
    color: '#1C2541',
  },
  stackedDataFirst: {
    display: 'block',
    lineHeight: '1.43',
    width: '100%',
  },
  statusAssigned: {
    color: 'blue',
  },
  statusCompleted: {
    color: 'green',
  },
  tabs: { minHeight: '15px' },
  tab: { minHeight: '15px', minWidth: '60px' },
  estateWorkItemMenuIconButton: {
    textAlign: 'right',
  },
}));

export const estateWorkItemStatusOptions = [
  {
    label: 'Assigned: Ready',
    value: EstateWorkItemStatus.AssignedReady,
  },
  {
    label: 'Completed',
    value: EstateWorkItemStatus.Completed,
  },
];

const TaskListTabs = {
  [EstateWorkItemStatus.AssignedReady]: 0,
  [EstateWorkItemStatus.Completed]: 1,
};

const TaskList = () => {
  const classes = useStyles();
  const [tabSelected, setTabSelected] = useState<EstateWorkItemStatus>(
    EstateWorkItemStatus.AssignedReady,
  );
  const [currentEstateWorkItem, setCurrentEstateWorkItem] = useState<EstateWorkItem | null>(null);
  const [selectedEstateWorkItemIds, setSelectedEstateWorkItemIds] = useState<string[]>([]);
  const [baseFilterModelFromDataGridTab, setBaseFilterModelFromDataGridTab] =
    useState<GridFilterModel>({
      items: [
        {
          field: TaskQueueDataGridColumns.Status,
          operator: 'equals',
          value: EstateWorkItemStatus.AssignedReady,
        },
      ],
    });
  const gridApiRef = useGridApiRef();

  // loading needs to be tracked separately from the queryResult object because the queryResult
  // is only updated after the query has completed, this means that queryResult.loading will always be false
  const [
    getAllEstateWorkItemsForTaskQueueDataGridLazyQuery,
    getAllEstateWorkItemsForTaskQueueDataGridLazyQueryResult,
  ] = useEstateWorkItem_GetAllEstateWorkItemsForTaskQueueDataGridLazyQuery({
    variables: {
      input: {
        paginationInput: {
          pageFilter: [
            {
              column: TaskQueueDataGridColumns.Status,
              operator: 'equals',
              value: EstateWorkItemStatus.AssignedReady,
            },
          ],
          page: defaultPaginationModel.page,
          pageSize: defaultPaginationModel.pageSize,
        },
      },
    },
    onError: (error) => {
      showAlert({
        message: 'Error getting all estate work items: ' + error.message,
        severity: NotificationSeverity.ERROR,
      });
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const [loadPortalMemberships] = usePortalMembership_GetPortalMembershipsByPortalIdLazyQuery({
    onError: (e) => {
      showAlert({
        message: 'Error getting all portal memberships: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [impersonate] = useAuth_ImpersonateMutation({
    onError: (e) => {
      showAlert({
        message: 'Error impersonating user: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const getTaskListCustomToolbar = () => {
    return <TaskListCustomToolbar tabSelected={tabSelected} />;
  };

  const handleImpersonateView = useCallback(
    async (type: string, estateWorkItem: EstateWorkItem) => {
      const estateId = estateWorkItem.estate!.id;
      const portalId = estateWorkItem.estate!.portal!.id;

      const { data: portalMembershipData } = await loadPortalMemberships({
        variables: {
          input: {
            portalId: portalId,
            permission: PortalPermissions.Admin,
          },
        },
      });

      // find the portal owner
      const portalOwner = portalMembershipData?.data.find(
        (portalMembership) => portalMembership.isPortalOwner,
      );

      if (!portalOwner || !portalOwner.userId) {
        showAlert({
          message: 'Error loading documents, no portal owner.',
          severity: NotificationSeverity.ERROR,
        });
        return;
      }

      const typeUrl: { [key: string]: string } = {
        documents: `/home/${portalId}/estate/${estateId}/estate-dashboard?tab=${EstateDashboardTab.VAULT}`, // send to the documents folder
        estate: `/home/${portalId}/assigned-tasks`, // send to the assigned tasks table
        portal: `/home/${portalId}/home`, // send to the portal page
      };

      await impersonate({
        variables: { userId: portalOwner.userId, returnUrl: '/admin/tasks' },
        onCompleted: () => {
          // react history.push() doesn't work because a page reload is needed for impersonate to take effect
          // TODO: Remove the need of a page refresh for impersonate to take effect.
          window.location.href = typeUrl[type];
        },
      });
    },
    [impersonate, loadPortalMemberships],
  );

  const columns = useMemo(() => {
    const tableColumns: GridColDef[] = [
      {
        disableColumnMenu: true,
        field: TaskQueueDataGridColumns.Purchased,
        flex: 6,
        filterable: false,
        headerName: 'Purchased',
        headerAlign: 'center',
        renderCell: ({ row }) =>
          row.estatePayment ? (
            <div className={classes.stackedDataFirst}>
              <div>
                <Typography variant="caption">
                  {format(Date.parse(row.estatePayment.createdAt), 'LLL d y')}
                </Typography>
              </div>
              <div className={classes.stackedDataFirst}>
                <Typography variant="caption">
                  {format(Date.parse(row.estatePayment.createdAt), 'h:mm aaa')}
                </Typography>
              </div>
            </div>
          ) : (
            'N/A'
          ),
        valueGetter: ({ row }) => row.estatePayment?.createdAt || '0',
      },
      {
        disableColumnMenu: true,
        field: TaskQueueDataGridColumns.Portal,
        flex: 6,
        filterable: true,
        headerName: 'Portal',
        headerAlign: 'left',
        renderCell: ({ row }) => row.estate.portal.name,
        valueGetter: ({ row }) => row.estate.portal.name,
      },
      {
        disableColumnMenu: true,
        field: TaskQueueDataGridColumns.Estate,
        flex: 6,
        filterable: true,
        sortable: false,
        headerName: 'Estate',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <EstateWorkItemEstateCell
            estateWorkItem={row as EstateWorkItem}
            handleImpersonateView={handleImpersonateView}
          />
        ),
        valueGetter: ({ row }) => row.estate.estateName,
      },
      {
        disableColumnMenu: true,
        field: TaskQueueDataGridColumns.EstateType,
        flex: 5,
        filterable: true,
        headerName: 'Estate Type',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <div className={classes.stackedDataFirst}>
            <Typography variant="caption">{row.estate.type}</Typography>
          </div>
        ),
        valueGetter: ({ row }) => row.estate.type,
      },
      {
        disableColumnMenu: true,
        field: TaskQueueDataGridColumns.Task,
        flex: 12,
        filterable: true,
        headerName: 'Task',
        headerAlign: 'left',
        renderCell: ({ row }) => <EstateWorkItemTaskCell estateWorkItem={row as EstateWorkItem} />,
        valueGetter: ({ row }) => row.workText,
      },
      {
        disableColumnMenu: true,
        field: TaskQueueDataGridColumns.Status,
        filterable: false, // filterable via the data grid tabs
        flex: 8,
        headerName: 'Status',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <EstateWorkItemTaskStatusCell
            estateWorkItem={row as EstateWorkItem}
            refetchEstateWorkItems={getQueryRefetchForDataGridQuery}
          />
        ),
        valueGetter: ({ row }) => {
          const estateWorkItem = row as EstateWorkItem;

          const statusLabel =
            EstateWorkItemStatusLabel[estateWorkItem.status] || estateWorkItem.status;
          return statusLabel;
        },
      },
      {
        disableColumnMenu: true,
        field: TaskQueueDataGridColumns.TaskStatus,
        flex: 9,
        headerName: 'Task Status',
        headerAlign: 'center',
        filterable: true,
        renderCell: ({ row }) => (
          <EstateWorkItemTaskSubStatusCell
            estateWorkItem={row as EstateWorkItem}
            refetchEstateWorkItems={getQueryRefetchForDataGridQuery}
            selectedEstateWorkItemIds={selectedEstateWorkItemIds}
            onEstateWorkItemSubStatusUpdate={() => setSelectedEstateWorkItemIds([])}
          />
        ),
        valueGetter: ({ row }) => {
          const estateWorkItem = row as EstateWorkItem;

          return EstateWorkItemSubStatusLabel[estateWorkItem.subStatus] || estateWorkItem.subStatus;
        },
      },
      {
        disableColumnMenu: true,
        field: TaskQueueDataGridColumns.ActionNeeded,
        filterable: false,
        flex: 6,
        headerName: 'Action Needed Tasks Reminder',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <EstateWorkItemActionNeededCell
            estateWorkItem={row as EstateWorkItem}
            onRefreshEstateWorkItem={getQueryRefetchForDataGridQuery}
          />
        ),
        valueGetter: ({ row }) => row.estate.lastActionNeededTasksReminderDate || '0',
      },
      {
        disableColumnMenu: true,
        field: TaskQueueDataGridColumns.TaskUpdated,
        filterable: false,
        flex: 5,
        headerName: 'Task Updated',
        headerAlign: 'left',
        renderCell: ({ row }) =>
          row.updatedAt ? (
            <div className={classes.stackedDataFirst}>
              <div>
                <Typography variant="caption">
                  {format(Date.parse(row.updatedAt), 'LLL d y')}
                </Typography>
              </div>
              <div className={classes.stackedDataFirst}>
                <Typography variant="caption">
                  {format(Date.parse(row.updatedAt), 'h:mm aaa')}
                </Typography>
              </div>
            </div>
          ) : (
            'N/A'
          ),
        valueGetter: ({ row }) => row.updatedAt || '0',
      },
      {
        disableColumnMenu: true,
        field: TaskQueueDataGridColumns.VaultUpdated,
        filterable: false,
        flex: 5,
        headerName: 'Vault Updated',
        headerAlign: 'left',
        renderCell: ({ row }) =>
          row.estate?.vaultLastUpdatedByUserDate ? (
            <div className={classes.stackedDataFirst}>
              <div>
                <Typography variant="caption">
                  {format(Date.parse(row.estate.vaultLastUpdatedByUserDate), 'LLL d y')}
                </Typography>
              </div>
              <div className={classes.stackedDataFirst}>
                <Typography variant="caption">
                  {format(Date.parse(row.estate.vaultLastUpdatedByUserDate), 'h:mm aaa')}
                </Typography>
              </div>
            </div>
          ) : (
            'N/A'
          ),
        valueGetter: ({ row }) => row.estate.vaultLastUpdatedByUserDate || '0',
      },
      {
        disableColumnMenu: true,
        field: TaskQueueDataGridColumns.NotesUpdated,
        filterable: false,
        flex: 5,
        headerName: 'Notes Updated',
        headerAlign: 'left',
        renderCell: ({ row }) =>
          row.estateWorkItemNotesLastUpdatedByUserDate ? (
            <div className={classes.stackedDataFirst}>
              <div>
                <Typography variant="caption">
                  {format(Date.parse(row.estateWorkItemNotesLastUpdatedByUserDate), 'LLL d y')}
                </Typography>
              </div>
              <div className={classes.stackedDataFirst}>
                <Typography variant="caption">
                  {format(Date.parse(row.estateWorkItemNotesLastUpdatedByUserDate), 'h:mm aaa')}
                </Typography>
              </div>
            </div>
          ) : (
            'N/A'
          ),
        valueGetter: ({ row }) => row.estateWorkItemNotesLastUpdatedByUserDate || '0',
      },
      {
        field: TaskQueueDataGridColumns.AdminPointPerson,
        flex: 4,
        disableColumnMenu: true,
        hideSortIcons: true,
        disableReorder: true,
        filterable: true,
        headerName: 'Admin Point Person',
        headerAlign: 'left',
        renderCell: (params: GridRenderCellParams) => {
          return (
            <AdminPointPerson
              estateWorkItem={params.row}
              refetchEstateWorkItems={getQueryRefetchForDataGridQuery}
            />
          );
        },
        valueGetter: ({ row }) => row.assignedTsa?.firstName || '',
      },
      {
        field: 'estateWorkItemAssignedTaskDrawerButton',
        width: 25,
        headerName: '',
        align: 'left',
        filterable: false,
        hideSortIcons: true,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <IconButton
            className={classes.estateWorkItemMenuIconButton}
            id={`estate-work-item-assigned-task-drawer-button-${row.id}`}
            onClick={() => setCurrentEstateWorkItem(row)}
          >
            <FirstPage className={classes.firstPageIcon} />
          </IconButton>
        ),
      },
    ];
    return tableColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    classes,
    baseFilterModelFromDataGridTab,
    getAllEstateWorkItemsForTaskQueueDataGridLazyQueryResult.refetch,
    handleImpersonateView,
    selectedEstateWorkItemIds,
  ]);

  const handleTabChange = async (_e: ChangeEvent<any>, newTabSelected: number) => {
    switch (newTabSelected) {
      case 0:
        setTabSelected(EstateWorkItemStatus.AssignedReady);
        setBaseFilterModelFromDataGridTab({
          items: [
            {
              field: TaskQueueDataGridColumns.Status,
              operator: 'equals',
              value: EstateWorkItemStatus.AssignedReady,
            },
          ],
        });
        break;
      case 1:
        setTabSelected(EstateWorkItemStatus.Completed);
        setBaseFilterModelFromDataGridTab({
          items: [
            {
              field: TaskQueueDataGridColumns.Status,
              operator: 'equals',
              value: EstateWorkItemStatus.Completed,
            },
          ],
        });
        break;
    }
  };

  useEffect(() => {
    // if the data grid filter is supposed to persist through tabs, then the filter model needs to be combined with the updated base before its sent to the server
    getQueryRefetchForDataGridQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseFilterModelFromDataGridTab]);

  const getFilterModel = () => {
    return {
      items: [
        ...(baseFilterModelFromDataGridTab?.items || []),
        ...(gridApiRef.current.state.filter.filterModel?.items || []),
      ],
      logicOperator:
        gridApiRef.current.state.filter.filterModel?.logicOperator ||
        baseFilterModelFromDataGridTab?.logicOperator,
    };
  };

  const getSortModel = () => {
    return gridApiRef.current.state.sorting.sortModel;
  };

  const getQueryRefetchForDataGridQuery = () => {
    const combinedFilterModel = getFilterModel();
    const sortModel = getSortModel();

    getAllEstateWorkItemsForTaskQueueDataGridLazyQueryResult.refetch({
      input: {
        paginationInput: {
          pageFilter: combinedFilterModel.items.map((item) => ({
            column: item.field,
            operator: item.operator,
            value: item.value,
          })) as EstateWorkItem_TaskQueueDataGridPageFilter[],
          pageSort:
            sortModel.length > 0
              ? {
                  column: sortModel[0].field,
                  sort: sortModel[0].sort,
                }
              : undefined,
          page: gridApiRef.current.state.pagination.paginationModel.page || 0,
          pageSize: gridApiRef.current.state.pagination.paginationModel.page || 100,
        },
      },
    });
  };

  return (
    <div>
      <Tabs
        value={TaskListTabs[tabSelected]}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleTabChange}
        className={classes.tabs}
      >
        <Tab className={classes.tab} label="Assigned: Ready" />
        <Tab className={classes.tab} label="Completed" />
      </Tabs>
      <ServerEnabledDataGrid<EstateWorkItem_TaskQueueDataGridPageFilter>
        apiRef={gridApiRef}
        gridStateKey={GridStateKey.TaskListGrid}
        className={classes.root}
        loading={getAllEstateWorkItemsForTaskQueueDataGridLazyQueryResult.loading}
        columns={columns}
        queryResult={getAllEstateWorkItemsForTaskQueueDataGridLazyQueryResult}
        checkboxSelection
        rowSelectionModel={selectedEstateWorkItemIds}
        onRowSelectionModelChange={(selectedEstateWorkItemIds) => {
          setSelectedEstateWorkItemIds(selectedEstateWorkItemIds as string[]);
        }}
        executeLazyQuery={getAllEstateWorkItemsForTaskQueueDataGridLazyQuery}
        disableColumnReorder
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        autoHeight={true}
        pagination
        baseFilterModelFromDataGridTab={baseFilterModelFromDataGridTab}
        initialState={{
          columns: {
            columnVisibilityModel: {
              amountPaid: false,
              couponCode: false,
              stripePaymentId: false,
            },
          },
        }}
        slots={{
          toolbar: getTaskListCustomToolbar,
        }}
      />
      {currentEstateWorkItem && (
        <EstateWorkItemDrawer
          estateWorkItem={currentEstateWorkItem}
          onClose={() => setCurrentEstateWorkItem(null)}
          refetchEstateWorkItems={() => getQueryRefetchForDataGridQuery()}
          handleImpersonateView={handleImpersonateView}
          setCurrentEstateWorkItem={setCurrentEstateWorkItem}
        />
      )}
    </div>
  );
};

export default TaskList;
